import { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import MainLayout from "./layouts/mainlayout";
import AuthCallback from "./auth/callback";
import { AuthErrorToast } from "./components/other/auth-error-toast";
import MaintenancePage from "./components/other/maintenance-page";
import {
  SiteSettingsProvider,
  useSiteSettings,
} from "./context/site-settings";
import { trackPageView } from "./services/analytics";

const Home = lazy(() => import("./pages/home"));
const Resume = lazy(() => import("./pages/resume"));
const Projects = lazy(() => import("./pages/projects"));
const Blog = lazy(() => import("./pages/blog"));
const About = lazy(() => import("./pages/about"));
const Admin = lazy(() => import("./pages/admin"));
const NotFound = lazy(() => import("./pages/not-found"));

// Redirect to /maintenance while maintenance mode is on, and track
// SPA navigations (location changes) as page views.
const RouteEffects = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings, loading } = useSiteSettings();

  useEffect(() => {
    if (!loading && settings.maintenance_mode && location.pathname !== "/admin") {
      navigate("/maintenance");
    }
  }, [location.pathname, navigate, settings.maintenance_mode, loading]);

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
};

// Renders the blog only when it is enabled in site settings;
// otherwise the route behaves like any other unknown URL.
const BlogGate = () => {
  const { settings, loading } = useSiteSettings();
  if (loading) return null;
  return settings.blog_enabled ? <Blog /> : <NotFound />;
};

function App() {
  const [authError, setAuthError] = useState<string | null>(null);

  // Listen for auth error messages in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Check for both error and authError params
    const error = params.get("error") || params.get("authError");
    if (error) {
      setAuthError(decodeURIComponent(error));
      // Remove error from URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  return (
    <>
      {authError && (
        <AuthErrorToast
          message={authError}
          onClose={() => setAuthError(null)}
        />
      )}
      <SiteSettingsProvider>
        <Router>
          <RouteEffects />
          <Suspense
            fallback={
              <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-white"></div>
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<MainLayout />}>
                <Route index element={<Home />} />
                <Route path="blog">
                  <Route index element={<BlogGate />} />
                  <Route path=":slug" element={<BlogGate />} />
                </Route>
                <Route path="projects" element={<Projects />} />
                <Route path="about" element={<About />} />
                <Route path="resume" element={<Resume />} />
                <Route path="admin" element={<Admin />} />
                <Route path="auth/callback" element={<AuthCallback />} />
                {/* Add the catch-all 404 route */}
                <Route path="*" element={<NotFound />} />
              </Route>
              <Route path="/maintenance" element={<MaintenancePage />} />
            </Routes>
          </Suspense>
        </Router>
      </SiteSettingsProvider>
    </>
  );
}

export default App;
